import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Crown, DollarSign, TrendingUp, Sparkles, Timer, BookOpen } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${window.location.origin}/api`;
const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const date = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const Kpi = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
    className={`glass-card rounded-2xl p-6 ${accent ? 'border-2 border-[#CAAB84]/50 bg-[#CAAB84]/5' : ''}`} data-testid={testId}>
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-xl bg-[#CAAB84]/15 flex items-center justify-center"><Icon className="h-5 w-5 text-[#B8956A]" /></div>
      <span className="text-[#AA9283] text-sm font-medium">{label}</span>
    </div>
    <p className="font-serif text-3xl text-[#726158]">{value}</p>
    {sub && <p className="text-[#B1897C] text-xs mt-1">{sub}</p>}
  </motion.div>
);

export const LifetimeOverview = () => {
  const { getAuthHeader } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/lifetime-overview`, { headers: getAuthHeader() }).then((r) => setD(r.data)).catch(() => setD(null));
  }, [getAuthHeader]);
  if (!d) return null;
  const bh = d.becoming_her;
  const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : '0%');
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="lifetime-kpi-grid">
        <Kpi icon={Crown} label="Lifetime Members" value={d.lifetime_members} sub="permanent access, never billed again" accent testId="kpi-lifetime-members" />
        <Kpi icon={DollarSign} label="Lifetime Revenue" value={fmt(d.lifetime_revenue)} sub={`${d.lifetime_sales} lifetime sales all-time`} accent testId="kpi-lifetime-revenue" />
        <Kpi icon={TrendingUp} label="Last 30 Days" value={fmt(d.last_30d.revenue)} sub={`${d.last_30d.count} sales · early access: ${d.early_access.count} (${fmt(d.early_access.revenue)})`} testId="kpi-last-30" />
        <Kpi icon={Timer} label="Trial → Lifetime" value={`${d.trial_conversion_rate}%`} sub={`${d.trial_upgrades} upgraded · ${d.active_trials} on trial now`} testId="kpi-trial-conversion" />
      </div>

      <div className="glass-card rounded-2xl p-6" data-testid="becoming-her-funnel">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-[#CAAB84]/15 flex items-center justify-center"><Sparkles className="h-5 w-5 text-[#B8956A]" /></div>
          <div>
            <h3 className="font-serif text-lg text-[#726158]">Becoming Her Journal → Dea</h3>
            <p className="text-[#AA9283] text-xs">Who came in through the free prompts, created a Dea account, and bought lifetime</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-5">
          {[['Prompt signups', bh.signups, ''], ['New Dea accounts after prompts', bh.dea_accounts, pct(bh.dea_accounts, bh.signups)], ['Bought Lifetime after prompts', bh.lifetime, pct(bh.lifetime, bh.signups)], ['Already members before prompts', bh.already_members, 'existing members who grabbed the journal']].map(([l, v, s]) => (
            <div key={l} className="bg-[#EAD8C2]/20 rounded-xl p-4" data-testid={`bh-funnel-${l.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <p className="text-xs text-[#AA9283] uppercase tracking-wide">{l}</p>
              <p className="text-2xl font-serif text-[#726158] mt-1">{v}</p>
              {s && <p className="text-xs text-[#B1897C]">{s}</p>}
            </div>
          ))}
        </div>
        {bh.by_source.length > 0 && (
          <p className="text-xs text-[#AA9283] mb-4">By video/source: {bh.by_source.map((b) => `${b.source} ${b.signups} → ${b.dea_accounts || 0} new accounts → ${b.lifetime} lifetime`).join(' · ')} · 90-Day Journal sales: {bh.journal_sales} (QR link coming soon)</p>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="bh-conversions-table">
            <thead><tr className="text-left text-[#AA9283] border-b border-[#EAD8C2]"><th className="py-2 pr-3">Name</th><th className="pr-3">Email</th><th className="pr-3">Got prompts</th><th className="pr-3">Signed up for Dea</th><th className="pr-3">Source</th><th>Access</th></tr></thead>
            <tbody>
              {bh.conversions.map((c) => (
                <tr key={c.email} className="border-b border-[#EAD8C2]/60 text-[#726158]">
                  <td className="py-2 pr-3">{c.first_name || '—'}</td><td className="pr-3">{c.email}</td><td className="pr-3">{date(c.lead_at)}</td>
                  <td className="pr-3 font-medium">{date(c.dea_signup_at)}</td><td className="pr-3">{c.source}</td>
                  <td>{c.converted_after_prompts
                    ? (c.access === 'lifetime' ? <span className="text-[#B8956A] font-medium">Converted → Lifetime ✓</span> : `Converted → ${c.access}`)
                    : <span className="text-[#AA9283]">Already a {c.access === 'lifetime' ? 'lifetime member' : c.access} before the prompts</span>}</td>
                </tr>
              ))}
              {bh.conversions.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-[#AA9283]"><BookOpen className="h-4 w-4 inline mr-1" />No prompt subscriber has created a Dea account yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
